import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

/**
 * Positions the host element below the active <li> of a sibling PrimeNG p-tabView.
 *
 * Usage:
 *   <div class="with-translate-btn">             <!-- needs position: relative -->
 *     <p-tabView [(activeIndex)]="activeIdx">...</p-tabView>
 *     <button class="translate-overlay-btn"
 *             [appTabIconAnchor]="activeIdx">    <!-- the host element is the icon button -->
 *       <i class="fa fa-magic"></i>
 *     </button>
 *   </div>
 *
 * The directive walks the host's parent subtree for `.p-tabview-nav`, picks the
 * `activeIndex`-th li, and writes `left` + `top` styles so the host's anchor
 * point sits at that li's bottom-center. CSS handles the centering transform.
 *
 * Geometry-agnostic: works for plain langtabs, scr-3, langtabs-long, etc.
 *
 * Inside p-dialogs the dialog finishes its open animation after `ngAfterViewInit`,
 * so the first measurement can land on a not-yet-settled rect. We use a
 * ResizeObserver on the parent + tab nav and a few delayed retries to catch
 * the layout settling — without those, the icon shows up off-position until
 * the user clicks a tab.
 */
@Directive({
  selector: '[appTabIconAnchor]',
})
export class TabIconAnchorDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appTabIconAnchor') activeIndex = 0;

  private rafId: number | null = null;
  private destroyed = false;
  private resizeObserver?: any;
  private retryTimers: any[] = [];

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.schedule();
    this.observeResizes();
    // A couple of follow-up measurements catch the dialog open animation
    // settling without hiding the icon — the icon may briefly nudge during
    // the open, but it's always visible and tab switches are instant.
    this.zone.runOutsideAngular(() => {
      [60, 180, 360].forEach((ms) => {
        this.retryTimers.push(setTimeout(() => this.update(), ms));
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeIndex) this.schedule();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.schedule();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.resizeObserver?.disconnect();
    this.retryTimers.forEach((t) => clearTimeout(t));
    this.retryTimers = [];
  }

  private observeResizes(): void {
    const RO = (window as any).ResizeObserver;
    if (typeof RO === 'undefined') return;
    const parent = this.el.nativeElement.parentElement;
    if (!parent) return;

    this.resizeObserver = new RO(() => this.schedule());
    this.resizeObserver.observe(parent);

    const tabNav = parent.querySelector('.p-tabview-nav') as HTMLElement | null;
    if (tabNav) this.resizeObserver.observe(tabNav);
  }

  private schedule(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        if (!this.destroyed) this.update();
      });
    });
  }

  private update(): void {
    if (this.destroyed) return;
    const host = this.el.nativeElement;
    const parent = host.parentElement;
    if (!parent) return;

    // Find the tab nav inside the same wrapper. If multiple p-tabViews share a
    // wrapper, callers should put each in its own `.with-translate-btn` parent.
    const tabNav = parent.querySelector('.p-tabview-nav');
    if (!tabNav) return;

    const li = tabNav.querySelectorAll(':scope > li')[this.activeIndex] as HTMLElement | undefined;
    if (!li) return;

    const parentRect = parent.getBoundingClientRect();
    const liRect = li.getBoundingClientRect();

    host.style.left = `${liRect.left - parentRect.left + liRect.width / 2}px`;
    host.style.top = `${liRect.bottom - parentRect.top}px`;
  }
}
